import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useClickOutside } from 'qampo-ui-library';
import type { LocationResponse, TreatmentPlanTemplateResponse } from '../../api/generated';
import MenuDotsIcon from '../../assets/icons/menu-dots.svg?react';
import { InterventionIconComponent } from '../InterventionIcon/InterventionIcon';
import { ColorBar } from '../UI';
import { TemplateStatsRow } from './templateBuilder/TemplateStatsRow';
import { countInterventions, sumReimbursement } from './templateStats';
import styles from './TreatmentPlanTemplateCard.module.css';

interface TreatmentPlanTemplateCardProps {
  template: TreatmentPlanTemplateResponse;
  locationsById: Record<string, LocationResponse>;
  onEdit: () => void;
  onDelete: () => void;
  onDuplicate: () => void;
}

export const TreatmentPlanTemplateCard = ({
  template,
  locationsById,
  onEdit,
  onDelete,
  onDuplicate
}: TreatmentPlanTemplateCardProps) => {
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useClickOutside(menuRef, () => setMenuOpen(false));

  const handleCardClick = () => {
    onEdit();
  };

  const templateGroups = template.weeks.flatMap((week) => week.groups);
  const weekCount = template.weeks.length;

  const locationNames = template.locationIds
    .map((id) => locationsById[id]?.name)
    .filter((name): name is string => typeof name === 'string');

  return (
    <div
      className={styles.card}
      onClick={handleCardClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onEdit(); }
      }}
    >
      <div className={styles.header}>
        <ColorBar color={template.strokeColor} variant="block" />
        {template.icon && (
          <span className={styles.icon} style={{ color: template.strokeColor }}>
            <InterventionIconComponent icon={template.icon} />
          </span>
        )}
        <span className={styles.name}>
          {template.name || <span className={styles.unnamed}>{t('config-unnamed-template')}</span>}
        </span>
        <div
          className={styles.menuWrapper}
          ref={menuRef}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            className={styles.menuButton}
            onClick={(e) => {
              e.stopPropagation();
              setMenuOpen((prev) => !prev);
            }}
            aria-label={t('config-template-menu')}
          >
            <MenuDotsIcon />
          </button>
          {menuOpen && (
            <div className={styles.dropdown}>
              <button
                className={styles.dropdownItem}
                onClick={() => {
                  setMenuOpen(false);
                  onEdit();
                }}
              >
                {t('edit')}
              </button>
              <button
                className={styles.dropdownItem}
                onClick={() => {
                  setMenuOpen(false);
                  onDuplicate();
                }}
              >
                {t('config-duplicate-template')}
              </button>
              <button
                className={`${styles.dropdownItem} ${styles.dropdownItemDelete}`}
                onClick={() => {
                  setMenuOpen(false);
                  onDelete();
                }}
              >
                {t('config-delete-template')}
              </button>
            </div>
          )}
        </div>
      </div>

      <div className={styles.chips}>
        {template.global && (
          <span className={`${styles.chip} ${styles.chipGlobal}`}>{t('config-card-global')}</span>
        )}
        {!template.global && locationNames.length === 0 && (
          <span className={`${styles.chip} ${styles.chipMuted}`}>
            {t('config-card-no-location')}
          </span>
        )}
        {locationNames.map((loc) => (
          <span key={loc} className={styles.chip}>
            {loc}
          </span>
        ))}
      </div>

      <TemplateStatsRow
        weekCount={weekCount}
        interventionCount={countInterventions(templateGroups)}
        reimbursement={sumReimbursement(templateGroups)}
        variant="card"
      />

      <div className={styles.weekPreview}>
        {template.weeks.map((week) => {
          const hasContent = week.groups.some((g) => g.interventions.length > 0);
          return (
            <div
              key={week.weekNumber}
              className={styles.weekDot}
              style={{
                backgroundColor: hasContent ? template.fillColor : 'transparent',
                border: hasContent
                  ? `1.5px solid ${template.strokeColor}`
                  : '1.5px solid var(--color-border-primary)'
              }}
              title={`${t('week-label')} ${week.weekNumber}`}
            />
          );
        })}
      </div>
    </div>
  );
};
